import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { Membership } from './api.ts';

export interface TenantContextValue {
  userId: string;
  /** The address the person signed in with; null when the sign-in carried none. */
  email: string | null;
  /**
   * Whether that address is verified (D83): null when Autho could not ask. Shown only as *email verified*, never
   * as *verified* alone, because the address is all that was checked.
   */
  emailVerified: boolean | null;
  tenantId: string;
  /** What this tenant is for. Decides the navigation and where `/` lands (Batch D.2). */
  kind: Membership['tenantKind'];
  membership: Membership;
  memberships: Membership[];
  canMutate: boolean;
  /** Whether Chat is open to this person (F441): until D51's gate passes, only to the people the service names. */
  chatOpen: boolean;
  /** Opens a profile on its landing page; with `stay`, on the page that asked (F548). */
  selectTenant: (tenantId: string, options?: { readonly stay?: boolean }) => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({
  value,
  children,
}: {
  value: TenantContextValue;
  children: ReactNode;
}) {
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant outside TenantProvider');
  return ctx;
}

/**
 * The tenant the API client sends in x-autho-tenant. Read at request time by the client middleware,
 * so it lives outside React state; the Gate updates it whenever the active membership changes.
 */
const active: { tenantId: string | null } = { tenantId: null };
export const activeTenant = {
  get: () => active.tenantId,
  set: (tenantId: string) => {
    active.tenantId = tenantId;
  },
};

const KEY = 'autho.tenant';

export function storedTenant(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function storeTenant(tenantId: string) {
  try {
    window.localStorage.setItem(KEY, tenantId);
  } catch {
    // Without storage the first membership is chosen on each visit.
  }
}
